import Cookies from 'js-cookie'
import { useCallback, useState } from 'react'

type TableState = { sortBy: string[]; sortDesc: boolean[] }

const COOKIE_TABLE_STATE = 'tasks-table-state'
const COOKIE_PAGE_SIZE = 'tasks-page-size'
const COOKIE_EXPIRES = 30

const DEFAULT_SORT_KEY = 'expired_on'
const DEFAULT_SORT_DIRECTION = 'asc' as const
const DEFAULT_PAGE_SIZE = 20
export const VALID_PAGE_SIZES = [20, 40, 60]

function parseCookieTableState(): TableState | null {
  try {
    const raw = Cookies.get(COOKIE_TABLE_STATE)
    return raw ? JSON.parse(raw) : null
  } catch {
    return null
  }
}

/**
 * タスクテーブルのソートとページサイズをCookieに永続化するフック
 *
 * ページリロード後も設定を復元する。
 */
export function useTasksTableState() {
  const [pageSize, setPageSize] = useState<number>(() => {
    const saved = Cookies.get(COOKIE_PAGE_SIZE)
    const parsed = saved ? Number.parseInt(saved, 10) : DEFAULT_PAGE_SIZE
    return VALID_PAGE_SIZES.includes(parsed) ? parsed : DEFAULT_PAGE_SIZE
  })

  const [sortKey, setSortKey] = useState<string | null>(() => {
    const saved: TableState | null = parseCookieTableState()
    return saved ? saved.sortBy?.[0] || null : DEFAULT_SORT_KEY
  })

  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>(() => {
    const saved: TableState | null = parseCookieTableState()
    return saved ? (saved.sortDesc?.[0] ? 'desc' : 'asc') : DEFAULT_SORT_DIRECTION
  })

  const handleSortChange = useCallback((key: string | null, direction: 'asc' | 'desc') => {
    setSortKey(key)
    setSortDirection(direction)

    const newState: TableState = {
      sortBy: key ? [key] : [],
      sortDesc: key ? [direction === 'desc'] : [],
    }

    const existing: TableState | null = parseCookieTableState()
    const isDifferent =
      !existing || existing.sortBy?.[0] !== newState.sortBy[0] || existing.sortDesc?.[0] !== newState.sortDesc[0]

    if (isDifferent) {
      Cookies.set(COOKIE_TABLE_STATE, JSON.stringify(newState), {
        secure: window.location.protocol === 'https:',
        expires: COOKIE_EXPIRES,
        path: '/',
      })
    }
  }, [])

  const handlePageSizeChange = useCallback((newPageSize: number) => {
    setPageSize(newPageSize)
    Cookies.set(COOKIE_PAGE_SIZE, String(newPageSize), {
      secure: window.location.protocol === 'https:',
      expires: COOKIE_EXPIRES,
      path: '/',
    })
  }, [])

  return {
    pageSize,
    sortKey,
    sortDirection,
    handleSortChange,
    handlePageSizeChange,
  }
}
